import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { fetchAdmin, jsonHeaders } from "../../utils/adminApi";
import { Plus, Save, Edit2, Trash2, Star } from "lucide-react";

const EMPTY = { name: "", location: "", rating: 5, text: "", tour: "", is_active: true };

export default function TestimonialsPanel({ testimonials, onRefresh }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  async function save(e) {
    e.preventDefault();
    if (editing) {
      await fetchAdmin(`/api/content/admin/testimonials/${editing}`, { method: "PUT", headers: jsonHeaders(), body: JSON.stringify(form) });
    } else {
      await fetchAdmin("/api/content/admin/testimonials", { method: "POST", headers: jsonHeaders(), body: JSON.stringify(form) });
    }
    setShowForm(false); setEditing(null); setForm(EMPTY); onRefresh();
  }

  function edit(t) {
    setEditing(t.testimonial_id);
    setForm({ name: t.name, location: t.location || "", rating: t.rating, text: t.text, tour: t.tour || "", is_active: t.is_active !== false });
    setShowForm(true);
  }

  async function remove(id) { if (!window.confirm("Delete this testimonial?")) return; await fetchAdmin(`/api/content/admin/testimonials/${id}`, { method: "DELETE" }); onRefresh(); }

  return (
    <div data-testid="testimonials-panel">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-white">Testimonials ({testimonials.length})</h2>
        <Button onClick={() => { setShowForm(!showForm); setEditing(null); setForm(EMPTY); }} className="bg-amber-600 hover:bg-amber-700 text-white rounded-xl" data-testid="add-testimonial-btn">
          <Plus className="w-4 h-4 mr-1.5" />{showForm ? "Cancel" : "Add Review"}
        </Button>
      </div>

      {showForm && (
        <form onSubmit={save} className="bg-stone-900/60 border border-stone-800 rounded-xl p-6 mb-6 space-y-4" data-testid="testimonial-form">
          <h3 className="text-white font-semibold">{editing ? "Edit Review" : "New Review"}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label className="text-stone-300">Name</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required className="mt-1 bg-stone-800 border-stone-700 text-white rounded-xl" data-testid="testimonial-name-input" /></div>
            <div><Label className="text-stone-300">Location</Label><Input value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))} className="mt-1 bg-stone-800 border-stone-700 text-white rounded-xl" /></div>
            <div><Label className="text-stone-300">Rating</Label><Input type="number" min="1" max="5" value={form.rating} onChange={e => setForm(p => ({ ...p, rating: parseInt(e.target.value) || 5 }))} className="mt-1 bg-stone-800 border-stone-700 text-white rounded-xl" /></div>
            <div><Label className="text-stone-300">Tour</Label><Input value={form.tour} onChange={e => setForm(p => ({ ...p, tour: e.target.value }))} className="mt-1 bg-stone-800 border-stone-700 text-white rounded-xl" /></div>
          </div>
          <div><Label className="text-stone-300">Review Text</Label><Textarea value={form.text} onChange={e => setForm(p => ({ ...p, text: e.target.value }))} required className="mt-1 bg-stone-800 border-stone-700 text-white rounded-xl" rows={3} /></div>
          <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl" data-testid="save-testimonial-btn"><Save className="w-4 h-4 mr-1.5" />{editing ? "Update Review" : "Save Review"}</Button>
        </form>
      )}

      <div className="space-y-3">
        {testimonials.map(t => (
          <div key={t.testimonial_id} className="bg-stone-900/60 border border-stone-800 rounded-xl p-5" data-testid={`testimonial-${t.testimonial_id}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-white font-medium">{t.name}</h3>
                  <div className="flex">{[...Array(t.rating)].map((_, i) => <Star key={i} className="w-3 h-3 fill-amber-500 text-amber-500" />)}</div>
                </div>
                <p className="text-stone-400 text-sm">{t.location} | {t.tour}</p>
                <p className="text-stone-300 text-sm mt-2 italic">"{t.text}"</p>
              </div>
              <div className="flex items-center gap-1">
                <Button onClick={() => edit(t)} variant="outline" size="sm" className="border-stone-700 text-amber-400 hover:bg-amber-600/10 hover:text-amber-300 rounded-lg text-xs" data-testid={`edit-testimonial-${t.testimonial_id}`}><Edit2 className="w-3.5 h-3.5 mr-1" />Edit</Button>
                <Button onClick={() => remove(t.testimonial_id)} variant="ghost" size="sm" className="text-stone-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          </div>
        ))}
        {testimonials.length === 0 && <p className="text-stone-500 text-center py-8">No testimonials yet</p>}
      </div>
    </div>
  );
}
